import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { BOOKING_MIX_SEGMENT_COLORS as BOOKING_MIX_COLORS } from 'pages/pga-coach/dashboard/dashboard-colors'
import AnalyticsCard from './analytics-card'
import useMixStyles from './booking-mix-styles'
import useStyles from '../styles'
import {
  buildMixSegments,
  formatCount,
  formatCurrency,
  getMixInsight,
  getRevenueInsight,
} from '../utils'

const MixTooltip = ({ active, payload, valueFormatter }) => {
  const classes = useStyles()

  if (!active || !payload?.length) return null

  return (
    <Box className={classes.chartTooltip}>
      <Typography component="div" className={classes.chartTooltipLabel}>
        {payload[0].name}
      </Typography>
      <Typography component="div" className={classes.chartTooltipValue}>
        {valueFormatter(payload[0].value)}
      </Typography>
    </Box>
  )
}

const MixPanel = ({ segments, valueFormatter }) => {
  const classes = useMixStyles()
  const chartData = segments.filter(segment => segment.value > 0)

  return (
    <Box className={classes.panelBody}>
      <Box className={classes.donut}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={39}
              outerRadius={57}
              paddingAngle={2}
            >
              {chartData.map(segment => (
                <Cell key={segment.id} fill={BOOKING_MIX_COLORS[segment.id]} />
              ))}
            </Pie>
            <Tooltip content={<MixTooltip valueFormatter={valueFormatter} />} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box className={classes.legend}>
        {segments.map(segment => (
          <Box key={segment.id} className={classes.legendItem}>
            <Box
              className={classes.legendSwatch}
              style={{ backgroundColor: BOOKING_MIX_COLORS[segment.id] }}
            />
            <span className={classes.legendLabel}>{segment.label}</span>
            <span className={classes.legendValue}>
              {valueFormatter(segment.value)}
            </span>
            <span className={classes.legendPercent}>{segment.percent}%</span>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const BookingMixCard = ({
  subtitle,
  revenueSubtitle = subtitle,
  privateBookings,
  groupBookings,
  privateRevenue,
  groupRevenue,
  errorMessage,
  loading,
  error,
}) => {
  const bookings = buildMixSegments({
    private: privateBookings,
    group: groupBookings,
  })
  const revenue = buildMixSegments({
    private: privateRevenue,
    group: groupRevenue,
  })

  return (
    <>
      <AnalyticsCard
        title="By bookings"
        subtitle={subtitle}
        insight={getMixInsight(bookings)}
        loading={loading}
        error={error}
        errorMessage={errorMessage}
        empty={bookings.total === 0}
        emptyMessage="No bookings."
      >
        <MixPanel
          segments={bookings.segments}
          valueFormatter={value => formatCount(value, 'booking')}
        />
      </AnalyticsCard>
      <AnalyticsCard
        title="By revenue"
        subtitle={revenueSubtitle}
        insight={getRevenueInsight(revenue)}
        loading={loading}
        error={error}
        errorMessage={errorMessage}
        empty={revenue.total === 0}
        emptyMessage="No revenue."
      >
        <MixPanel segments={revenue.segments} valueFormatter={formatCurrency} />
      </AnalyticsCard>
    </>
  )
}

export default BookingMixCard
